import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {CrossIcon, Dropdown, HexagonText, InputLabel, Tag} from 'web-components';
import _ from 'lodash';
import {
	DropdownOption,
	FlexGroup,
	HoverShow,
	Item,
	PreviewSelector,
	Selections,
	SelectionsHeader,
	SelectionsHeaderActions,
} from './PreviewSelector.styled';
import {Scrollbars} from 'react-custom-scrollbars';
import Search from '../../../../../components/new-ui/search';
import {createFragmentContainer, graphql} from 'react-relay';
import {profilePicSrc} from '../../../../../constants';
import {personIsVirtualUser} from '../../../util/PermissionsUtil';

const PersonFilterType = {
	ACTIVE_PEOPLE: 'active_people',
	USERS: 'users',
	VIRTUAL_RESOURCES: 'virtual_resources',
	DEACTIVATED: 'deactivated',
};

const PersonsPreviewSelector = ({persons, selectedPersons, onChange, selectionHeaderLabel, hideSelectionHeaderActions = false}) => {
	const {formatMessage} = useIntl();
	const personById = _.keyBy(persons, person => person.id);
	const [searchValue, setSearchValue] = useState('');
	const [personFilterType, setPersonFilterType] = useState(PersonFilterType.ACTIVE_PEOPLE);

	if (!selectionHeaderLabel) {
		selectionHeaderLabel = formatMessage({id: `common.selected`});
	}

	const onChangePersonFilter = selectedItems => {
		setPersonFilterType(selectedItems[0]);
	};

	const getUserType = person => {
		if (person.active) {
			if (!personIsVirtualUser(person)) {
				return formatMessage({id: 'filter_type.resource_type.active'});
			} else {
				return formatMessage({id: 'filter_type.resource_type.virtual_resource'});
			}
		} else {
			return formatMessage({id: 'filter_type.resource_type.deactivated'});
		}
	};

	const personTypeFilter = person => {
		if (personFilterType === PersonFilterType.ACTIVE_PEOPLE) {
			return person.active;
		} else if (personFilterType === PersonFilterType.USERS) {
			return person.active && !personIsVirtualUser(person);
		} else if (personFilterType === PersonFilterType.VIRTUAL_RESOURCES) {
			return person.active && personIsVirtualUser(person);
		} else if (personFilterType === PersonFilterType.DEACTIVATED) {
			return !person.active;
		}
	};

	const getSearchString = person =>
		[person.fullName, person.role?.name, getUserType(person)].map(string => string?.toLowerCase()).join(' ');

	const searchFilter = person => {
		return getSearchString(person).includes(searchValue.toLowerCase());
	};

	const selected = selectedPersons
		.map(selectedPerson => personById[selectedPerson])
		.filter(Boolean)
		.filter(searchFilter)
		.filter(personTypeFilter)
		.reverse();

	const onSelectionUpdate = (_, peopleIds) => {
		onChange(peopleIds);
	};

	const onRemove = person => {
		onChange(selectedPersons.filter(item => item !== person.id));
	};

	return (
		<PreviewSelector>
			<InputLabel
				text={formatMessage({id: 'settings_skills.users'})}
				child={
					<Dropdown
						isClearable
						isMultiSelect
						usePortal
						hideSelected
						onSelect={onSelectionUpdate}
						onRemove={onSelectionUpdate}
						selectedItems={selectedPersons}
						name={formatMessage({id: 'settings_skills.assign_people'})}
						cy={'skill-persons'}
					>
						{persons.map((person, index) => (
							<DropdownOption
								data-cy={`person-option-${index}`}
								key={person.id}
								value={person.id}
								searchString={getSearchString(person)}
							>
								<HexagonText
									text={person.fullName}
									subText={person.role?.name || ''}
									imgUrl={person.profilePictureId ? profilePicSrc(person.profilePictureId) : null}
								/>
								<Tag color={'#a5a5ae'} name={getUserType(person)} />
							</DropdownOption>
						))}
					</Dropdown>
				}
			/>
			<Selections data-cy={'selected-persons'}>
				<SelectionsHeader>
					<InputLabel text={`${selectionHeaderLabel} (${selected.length})`} />
					{!hideSelectionHeaderActions && (
						<SelectionsHeaderActions>
							<Search dataCy={'selected-search'} key={`selected-search`} value={searchValue} onChange={setSearchValue} />
							<Dropdown
								dataCy={'selected-filter'}
								name={formatMessage({id: `settings_skills.filter_person.${personFilterType}`})}
								selectedItems={[personFilterType]}
								onSelect={onChangePersonFilter}
								usePortal
							>
								{Object.values(PersonFilterType).map(personFilterType => {
									return (
										<Dropdown.SingleText key={personFilterType} value={personFilterType}>
											{formatMessage({id: `settings_skills.filter_person.${personFilterType}`})}
										</Dropdown.SingleText>
									);
								})}
							</Dropdown>
						</SelectionsHeaderActions>
					)}
				</SelectionsHeader>
				<Scrollbars style={{height: '200px'}}>
					{selected.map((person, index) => (
						<Item data-cy={`selected-person-${index}`}>
							<HexagonText
								key={person.id}
								value={person.id}
								searchString={person.fullName}
								text={person.fullName}
								subText={person.role?.name || ''}
								imgUrl={person.profilePictureId ? profilePicSrc(person.profilePictureId) : null}
							/>
							<FlexGroup>
								<Tag color={'#a5a5ae'} name={getUserType(person)} />
								<HoverShow>
									<CrossIcon size={CrossIcon.SIZE.STANDARD} color={'#000'} onClick={() => onRemove(person)} />
								</HoverShow>
							</FlexGroup>
						</Item>
					))}
				</Scrollbars>
			</Selections>
		</PreviewSelector>
	);
};
export default createFragmentContainer(PersonsPreviewSelector, {
	persons: graphql`
		fragment PersonsPreviewSelector_persons on Person @relay(plural: true) {
			id
			fullName
			profilePictureId
			active
			permissions
			role {
				name
			}
		}
	`,
});
